import React, { useState, useEffect } from 'react';
import { ModalDialog } from '@uhg-abyss/web/ui/ModalDialog';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useForm } from '@uhg-abyss/web/hooks/useForm';
import { Layout } from '@uhg-abyss/web/ui/Layout';
import { Button } from '@uhg-abyss/web/ui/Button';
import { FormProvider } from '@uhg-abyss/web/ui/FormProvider';
import { v4 as uuidv4 } from 'uuid';
import { Alert } from '@uhg-abyss/web/ui/Alert';
import { useUploadFile, resetUploadFile } from '@src/hooks/useUploadFile';
import {
  useDownloadFile,
  resetDownloadFileAPI,
} from '@src/hooks/useDownloadFile';
import { useDeleteFile, resetDeleteFiles } from '@src/hooks/useDeleteFile';
import { styled } from '@uhg-abyss/web/tools/styled';
import { LagoonContext } from '@src/AppContext';
import { CreateCommunicationFormFields } from './CreateCommunicationFormFields/CreateCommunicationFormFields';
import {
  useSaveDraftAsApi,
  resetSaveDraftAsApi,
} from '@src/hooks/useSaveAsDraftApi';
import {
  useDeleteCommunicationAPI,
  resetDeleteCommunicationAPI,
} from '@src/hooks/useDeleteCommunicationAPI';
import { resetDraftApi } from '@src/hooks/useGetDraftApi';
import { useGetCommunicationAPI } from '@src/hooks/useGetCommunicationAPI';
import {
  useSendCommunicationApi,
  resetSendCommunicationApi,
} from '@src/hooks/useSendCommunicationApi';

const Space = styled('div', {
  paddingTop: '18px',
});

export const CreateCommunicationModal = ({
  showModal,
  modalOnClose,
  setConfirmationTitle,
  setShowConfirmationModal,
}) => {
  const [saveDraft, callSaveAsDraft] = useSaveDraftAsApi();
  const [sendCommunication, callSendCommunication] = useSendCommunicationApi();
  const [, getCommunicationAPI] = useGetCommunicationAPI();
  const [, resetFileDownload] = resetDownloadFileAPI();
  const [deleteCommunication, deleteCommunicationAPI] =
    useDeleteCommunicationAPI();
  const [deleteFiles, deleteUploadedFiles] = useDeleteFile();
  const [, resetDeleteFilesData] = resetDeleteFiles();
  const [, resetDeleteCommunication] = resetDeleteCommunicationAPI();
  const [s3File, downloadS3File] = useDownloadFile();
  const [, resetSaveAsDraft] = resetSaveDraftAsApi();
  const [, resetSendCommunication] = resetSendCommunicationApi();
  const [, resetDraftCommunication] = resetDraftApi();
  const [, resetUploadFiles] = resetUploadFile();
  const [commAction, setCommAction] = useState('');
  const [sendComm, setSendComm] = useState(false);
  // const [fileStatus, setFileStatus] = useState('');
  const communicationsList = useSelector((state) => {
    return state.GET_COMMUNICATION_API.data;
  });
  const [upload, uploadFile] = useUploadFile();
  const [fileUploadComplete, setFileUploadComplete] = useState(false);
  const [draftFiles, setDraftFiles] = useState([]);
  const [downloadedFiles, setDownloadedFiles] = useState([]);
  // Maps filename -> the S3 key it was already uploaded under, so unchanged
  // existing files can be preserved instead of re-uploaded as a duplicate.
  const [existingFileKeysByName, setExistingFileKeysByName] = useState({});
  const [callSaveDraft, setCallSaveDraft] = useState(false);
  const userInfo = useSelector((state) => {
    return state.GET_USER_INFO.data;
  });
  const draftStore = useSelector((state) => {
    return state.GET_DRAFT_API;
  });
  const draftCommunication = draftStore.data;
  const form = useForm();
  const [serverErrorMessage, setServerErrorMessage] = useState({
    header: '',
    message: '',
  });
  const [displayError, setDisplayError] = useState(false);
  const [deleteFlowTriggered, setDeleteFlow] = useState(false);
  const dateFieldValue = new Date(draftCommunication?.date).setHours(
    0,
    0,
    0,
    0
  );
  const newDate = new Date();
  const [reuploadFiles, setReuploadFiles] = useState(false);
  const commSubject = form.watch('subject');
  const commDate = form.watch('date');
  const currentDate = new Date(newDate).setHours(0, 0, 0, 0);
  const fileFormLength = form.getValues('fileUpload');
  const lagoonContext = React.useContext(LagoonContext);
  const errorMessages = lagoonContext?.getTable('error-messages').getRow();
  // FileUpload keeps failed (e.g. customValidation) files in the RHF field
  // value alongside valid ones, so cross-check against draftFiles' status to
  // drop only the individual files that failed validation, not the whole list.
  // Match by object identity (not name) - a rejected duplicate shares its name
  // with a legitimate kept file, so a name match would wrongly drop both.
  const getCurrentFiles = () => {
    const files = form.getValues('fileUpload') || [];
    return files.filter((file) => {
      return !draftFiles.some((queuedFile) => {
        return queuedFile?.status === 'error' && queuedFile?.file === file;
      });
    });
  };

  // Files already uploaded to S3 for this draft don't need to be re-uploaded
  // (and re-keyed) when left untouched; only genuinely new files do.
  const getNewFilesToUpload = () => {
    return getCurrentFiles().filter((file) => {
      return !existingFileKeysByName[file?.name];
    });
  };

  // Original attachment keys no longer referenced by any currently kept
  // (preserved or re-uploaded) file - these are the only ones safe to delete.
  const getStaleFileIds = () => {
    const { fileIds: draftFileIds = [] } = draftCommunication || {};
    const keptFileIds = getCurrentFiles()
      .map((file) => {
        return existingFileKeysByName[file?.name];
      })
      .filter(Boolean);
    return draftFileIds.filter((fileId) => {
      return !keptFileIds.includes(fileId);
    });
  };
  const showConfirmationModal =
    !!(!saveDraft?.loading && !saveDraft?.error && saveDraft?.data?.message) ||
    !!(
      !sendCommunication?.loading &&
      !sendCommunication?.error &&
      sendCommunication?.data?.message
    );

  const onModalClose = () => {
    resetSaveAsDraft();
    resetSendCommunication();
    resetDraftCommunication();
    resetUploadFiles();
    resetDeleteFilesData();
    resetDeleteCommunication();
    setDisplayError(false);
    resetFileDownload();
    form.reset();
    setDraftFiles([]);
    setDownloadedFiles([]);
    setExistingFileKeysByName({});
    modalOnClose(false);
    setDeleteFlow(false);
  };

  const getErrorMessage = (messageKey) => {
    const messageExist = errorMessages.find((message) => {
      return message?.active && message?.key === messageKey;
    });
    if (messageExist) {
      setDisplayError(true);
      const { header = '', message = '' } = messageExist || {};
      setServerErrorMessage({ header, message });
    }
  };

  useEffect(() => {
    if (
      (draftFiles.length > 0 || draftFiles.length === fileFormLength?.length) &&
      !reuploadFiles
    ) {
      setReuploadFiles(true);
    }
  }, [draftFiles]);

  useEffect(() => {
    if (
      !deleteCommunication.loading &&
      deleteCommunication.data.message &&
      !deleteCommunication?.error
    ) {
      getCommunicationAPI({ userInfo: { data: userInfo }, allMessages: true });
      const { fileIds = [] } = draftCommunication || {};
      if (fileIds?.length) {
        const fileKeyList = fileIds?.map((fileId) => {
          return {
            Key: `apimarketplace/${fileId}`,
          };
        });
        deleteUploadedFiles({ userInfo, fileKeyList });
      } else {
        onModalClose();
      }
    } else if (deleteCommunication?.error) {
      getErrorMessage('DELETE_DRAFT_FAILED');
    }
  }, [deleteCommunication]);

  useEffect(() => {
    if (s3File?.error) {
      setDisplayError(true);
      getErrorMessage('FILE_DOWNLOAD_FAILED');
    }
    if (
      s3File?.data?.length === draftCommunication?.fileIds?.length &&
      !s3File.containsFiles
    ) {
      const fileKeysByName = {};
      const mimeType = 'application/pdf';
      // Build every file in parallel and hand off the complete set in one
      // update - setting downloadedFiles per-file (cumulatively) made
      // FormFields re-dispatch the whole list on each arrival, resubmitting
      // already-added files and getting them flagged as duplicates of themselves.
      Promise.all(
        s3File.data.map((fileData) => {
          fileKeysByName[fileData.filename] = fileData.fileKey;
          return fetch(`data:${mimeType};base64,${fileData.fileContent}`)
            .then((blobRes) => {
              return blobRes.blob();
            })
            .then((blobRes) => {
              return new File([blobRes], fileData.filename, {
                type: mimeType,
              });
            });
        })
      ).then((fileResponse) => {
        form.setValue('fileUpload', fileResponse);
        // FileUpload (v2.4) only reflects files dropped/selected through its own
        // input, so hand off a fresh reference for FormFields to inject via DOM.
        setDownloadedFiles(fileResponse);
      });
      setExistingFileKeysByName(fileKeysByName);
    }
  }, [s3File]);

  const confirmationModalClick = (modalTitle) => {
    setShowConfirmationModal(true);
    setConfirmationTitle(modalTitle);
    // if (
    //   !reuploadFiles ||
    //   (reuploadFiles &&
    //     (!draftCommunication.fileIds ||
    //       draftCommunication.fileIds.length === 0))
    // ) {
    onModalClose();
    // }
    form.reset();
  };

  const confModalTitle =
    commAction === 'draft'
      ? 'Your draft has been saved'
      : 'Your communication has been sent';

  useEffect(() => {
    if (!deleteFiles.loading && deleteFiles.data.message && reuploadFiles) {
      if (showConfirmationModal) {
        confirmationModalClick(confModalTitle);
      }
    } else if (
      !deleteFiles.loading &&
      deleteFiles.data.message &&
      deleteFlowTriggered
    ) {
      const deleteTitle = 'Your draft has been deleted';
      confirmationModalClick(deleteTitle);
    }
  }, [deleteFiles]);

  useEffect(() => {
    const files = getNewFilesToUpload();
    if (
      !upload.loading &&
      files?.length &&
      files?.length === upload.data.length
    ) {
      setFileUploadComplete(true);
    }

    if (upload.error) {
      setDisplayError(true);
      getErrorMessage('FILE_UPLOAD_FAILED');
    }
  }, [upload]);

  useEffect(() => {
    if (fileUploadComplete) {
      if (commAction === 'draft') {
        setCallSaveDraft(true);
      } else {
        setSendComm(true);
      }
    }
  }, [fileUploadComplete]);

  useEffect(() => {
    if (callSaveDraft || sendComm) {
      setCallSaveDraft(false);
      setSendComm(false);
      const {
        isFilesAvailable = '',
        audience = '',
        date = '',
        subject = '',
        topic = '',
        actionByUnitedHealthCare = '',
        clientActions = '',
      } = form.getValues();

      let fileIds = [];
      let files;
      const values = {
        id: draftCommunication.id,
        action: commAction,
        communicationsDetails: {
          audience,
          date,
          subject,
          topicBody: topic,
          actionByUhgBody: actionByUnitedHealthCare,
          clientActionsBody: clientActions,
          fileUploadAvailable: isFilesAvailable === 'yes',
        },
      };

      if (isFilesAvailable === 'yes') {
        // Exclude files that failed validation so they aren't sent as raw
        // attachments to the send-communication API.
        files = getCurrentFiles();
        if (reuploadFiles) {
          // Keep the original S3 key for untouched existing files and only pull
          // fresh keys (in order) for the files that were actually re-uploaded.
          const newFileIds = upload.data.map((fileDetails) => {
            return fileDetails.s3FileKey;
          });
          let newFileIndex = 0;
          fileIds = getCurrentFiles().map((file) => {
            const existingKey = existingFileKeysByName[file?.name];
            if (existingKey) {
              return existingKey;
            }
            const newKey = newFileIds[newFileIndex];
            newFileIndex += 1;
            return newKey;
          });
        } else {
          fileIds = draftCommunication.fileIds || [];
        }

        values.communicationsDetails.fileIds = fileIds;
      }

      if (commAction === 'draft') {
        callSaveAsDraft({
          variables: { body: values },
          userInfo,
        });
      } else {
        callSendCommunication({
          variables: { request: values, files },
          userInfo,
        });
      }
    }
  }, [callSaveDraft, sendComm]);

  const isConfModalShown = () => {
    return (
      showConfirmationModal && (!reuploadFiles || !getStaleFileIds().length)
    );
  };

  useEffect(() => {
    // removing the files in the draft communication,they have re-aploaded in the previous uploadFile call
    const staleFileIds = getStaleFileIds();
    if (showConfirmationModal && reuploadFiles && staleFileIds.length) {
      const fileKeyList = staleFileIds.map((fileId) => {
        return {
          Key: `apimarketplace/${fileId}`,
        };
      });
      deleteUploadedFiles({ fileKeyList });
    }

    const showConfModal = isConfModalShown();
    if (showConfModal) {
      confirmationModalClick(confModalTitle);
    }

    if (sendCommunication?.error || saveDraft?.error) {
      setDisplayError(true);
      if (commAction === 'draft') {
        getErrorMessage('SAVE_DRAFT_FAILED');
      } else {
        getErrorMessage('SEND_COMMUNICATION_FAILED');
      }
    }
  }, [saveDraft, sendCommunication]);

  useEffect(() => {
    if (draftCommunication && draftCommunication.id) {
      if (dateFieldValue === currentDate || dateFieldValue > currentDate) {
        form.setValue('date', draftCommunication.date);
      }
      form.setValue('audience', draftCommunication.audience);
      form.setValue('subject', draftCommunication.subject);
      form.setValue('topic', draftCommunication.topicBody);
      form.setValue(
        'actionByUnitedHealthCare',
        draftCommunication.actionByUhgBody
      );
      form.setValue('clientActions', draftCommunication.clientActionsBody);

      if (!draftCommunication.fileUploadAvailable) {
        form.setValue('isFilesAvailable', '');
      } else if (draftCommunication.fileUploadAvailable) {
        form.setValue('isFilesAvailable', 'yes');

        const fileIdsFromDraft = draftCommunication.fileIds;
        if (fileIdsFromDraft?.length > 0) {
          fileIdsFromDraft.forEach((fileId) => {
            downloadS3File({ fileKey: fileId, userInfo });
          });
        }
      } else if (draftCommunication.fileUploadAvailable === false) {
        form.setValue('isFilesAvailable', 'no');
      }
    }
  }, [draftCommunication]);

  const readFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        const fileContent = reader.result.split(',')[1];
        resolve(fileContent);
      };

      reader.onerror = (error) => {
        reject(error);
      };

      reader.readAsDataURL(file);
    });
  };

  const onSave = () => {
    if (commSubject && commDate) {
      const { isFilesAvailable = '' } = form.getValues();
      const filesToUpload = getNewFilesToUpload();
      if (isFilesAvailable === 'yes' && filesToUpload.length && reuploadFiles) {
        filesToUpload.forEach((file) => {
          const fileContent = readFile(file);
          return fileContent.then((content) => {
            const key = `${uuidv4()}_${file.name}`;
            const request = {
              fileContent: content,
              fileName: file.name,
              contentType: file.type,
              fileKey: key,
            };
            uploadFile({ request, userInfo });
          });
        });
      } else if (commAction === 'draft') {
        setCallSaveDraft(true);
      } else {
        setSendComm(true);
      }
    } else {
      form.trigger(['subject', 'date']);
    }
  };

  const onSubmit = () => {
    resetUploadFiles();
    setFileUploadComplete(false);
    setDisplayError(false);
    setCommAction('send');
    onSave();
  };

  const deleteDraft = () => {
    const { id = '' } = draftCommunication || {};
    if (id) {
      deleteCommunicationAPI({ userInfo, ids: [id] });
    } else {
      onModalClose();
    }
  };

  return (
    <ModalDialog
      closeOnClickOutside={false}
      header="Create communication"
      isOpen={showModal}
      onClose={onModalClose}
      css={{
        'abyss-modal-base-header-container': {
          color: '#002677',
          fontSize: '28.83px',
        },
      }}
    >
      <div>
        <div style={{ fontSize: '15px' }} id="modalDescription">
          Please fill out all the fields as needed to create the communication.
          Select the designated audience the email is being sent out to, as well
          as upload any attachments that need to be sent out with the
          communication to the clients. The communication should not include any
          PHI/PII Information and is limited to a 2.5 MB limit per attachment.
          <p>
            The communication can be saved as a draft to be sent out on a future
            date, as long as the date is marked as the current date for the
            communication to be sent out. Limit 5 drafts.
          </p>
        </div>
        <FormProvider state={form} onSubmit={onSubmit}>
          <CreateCommunicationFormFields
            setDraftFiles={setDraftFiles}
            downloadedFiles={downloadedFiles}
            draftFiles={draftFiles}
          />
          {displayError ? (
            <Alert title={serverErrorMessage?.header}>
              {serverErrorMessage?.message}
            </Alert>
          ) : null}
          <Space />
          <Layout.Group alignLayout="right">
            <Button
              id="deleteDraftButton"
              type="button"
              variant="outline"
              color="destructive"
              fontSize="15px"
              onClick={() => {
                deleteDraft();
                setDeleteFlow(true);
              }}
              isLoading={
                (deleteFlowTriggered &&
                  deleteCommunication.loading &&
                  !(callSaveDraft || sendComm)) ||
                (deleteFlowTriggered && deleteFiles.loading)
              }
              ariaLoadingLabel="Deleting communication"
            >
              Delete
            </Button>
            <Button
              id="saveDraftButton"
              type="button"
              variant="outline"
              fontSize="15px"
              onClick={() => {
                resetUploadFiles();
                setFileUploadComplete(false);
                setDisplayError(false);
                setCommAction('draft');
                onSave();
              }}
              isLoading={
                commAction === 'draft' &&
                (upload.loading ||
                  saveDraft.loading ||
                  deleteCommunication.loading ||
                  deleteFiles.loading)
              }
              ariaLoadingLabel="Saving communication"
              isDisabled={
                !draftCommunication?.id &&
                communicationsList?.draftMessages?.length > 4
              }
            >
              Save as draft
            </Button>
            <Button
              id="sentCommunicationButton"
              type="submit"
              fontSize="15px"
              isDisabled={
                new Date(commDate).setHours(0, 0, 0, 0) !== currentDate
              }
              isLoading={
                commAction === 'send' &&
                (upload.loading ||
                  sendCommunication.loading ||
                  deleteCommunication.loading ||
                  deleteFiles.loading)
              }
              ariaLoadingLabel="Sending communication"
            >
              Send
            </Button>
          </Layout.Group>
        </FormProvider>
      </div>
    </ModalDialog>
  );
};

CreateCommunicationModal.propTypes = {
  showModal: PropTypes.bool.isRequired,
  modalOnClose: PropTypes.func.isRequired,
  setConfirmationTitle: PropTypes.func.isRequired,
  setShowConfirmationModal: PropTypes.func.isRequired,
};
